import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from '../../i18n';
import { cardSx, colors, radius } from '../../theme/tokens';
import { formatCurrency } from '../../utils/currency';
import { formatDocument } from '../../utils/document';
import { formatPhoneDisplay } from '../../utils/phone';
import type { Plan } from '../../types/api';

/**
 * Resumo do pedido do checkout com cartão.
 *
 * Paridade com a coluna esquerda do `cartao_credito.blade.php`: faixa superior
 * na cor da marca com o plano escolhido e, abaixo, valor, linha e documento.
 *
 * Todos os dados exibidos vêm da SESSÃO (plano validado, linha validada e
 * documento devolvido pela API) — nunca da querystring e nunca de digitação do
 * cliente. É a mesma regra do legado, que montava a tela a partir de
 * `dados_cliente_linha` e `planos_disponiveis`.
 */

interface OrderSummaryProps {
  plan: Plan;
  /** Linha validada, somente dígitos. */
  line: string;
  /**
   * Documento do titular já extraído da sessão; `null` quando a API não
   * devolveu. Nome propositalmente diferente de `document` para não sombrear o
   * global do DOM.
   */
  holderDocument: string | null;
}

interface SummaryRowProps {
  label: string;
  value: string;
  /** Linha separada por um filete, como o documento no legado. */
  divided?: boolean;
}

function SummaryRow({ label, value, divided = false }: SummaryRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 2,
        fontSize: '0.875rem',
        ...(divided
          ? { borderTop: `1px solid ${colors.borderSoft}`, pt: 1.5, mt: 0.5 }
          : null),
      }}
    >
      <Typography component="span" sx={{ fontSize: '0.875rem', color: colors.textSecondary }}>
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: '0.875rem',
          fontWeight: 700,
          color: colors.textPrimary,
          textAlign: 'right',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export function OrderSummary({ plan, line, holderDocument }: OrderSummaryProps) {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography
        component="h2"
        sx={{ fontSize: '1.125rem', fontWeight: 700, color: colors.textPrimary }}
      >
        {t.creditCard.summaryTitle}
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: '0.875rem', color: colors.textSecondary }}>
        {t.creditCard.summarySubtitle}
      </Typography>

      <Box sx={{ ...cardSx, mt: 2, overflow: 'hidden', borderRadius: radius.md }}>
        <Box
          sx={{
            p: 2,
            textAlign: 'center',
            backgroundColor: colors.brandHeader,
            color: colors.white,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              opacity: 0.9,
            }}
          >
            {t.creditCard.selectedPlan}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.2 }}>
            {plan.nome_plano || plan.plano}
          </Typography>
        </Box>

        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <SummaryRow
            label={t.creditCard.amount}
            value={`${t.plans.currency} ${formatCurrency(plan.valor)}`}
          />
          <SummaryRow label={t.creditCard.line} value={formatPhoneDisplay(line)} />
          {holderDocument ? (
            <SummaryRow
              label={t.creditCard.document}
              value={formatDocument(holderDocument)}
              divided
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
